import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import InArticleAd from "../ads/InArticleAd";
import SocialIcon from "../components/SocialIcon";
import { ResponsiveComparisonTable, LinkPills } from "../components/ContentBlocks";
import { seoConfig } from "../data/seoConfig";
import { getRoboticsSeoPageByPath, roboticsSeoClusterPages } from "../data/roboticsSeoCluster";
import { getRoboticsResearchEnhancement } from "../data/roboticsResearchEnhancements";

function NotFoundArticle() {
  return (
    <section className="site-shell py-16">
      <div className="soft-card p-8">
        <h1 className="text-3xl font-black text-neutral-950">Robotics guide not found</h1>
        <p className="mt-3 text-neutral-600">Open the robotics hub to continue reading TechniaHQRobot guides.</p>
        <Link to="/robotics" className="btn-primary mt-6">Open robotics hub</Link>
      </div>
    </section>
  );
}

function KeyFacts({ facts }) {
  if (!facts?.length) return null;
  return (
    <section className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-5 sm:p-6">
      <h2 className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">Key facts</h2>
      <ul className="mt-4 grid gap-3 text-sm leading-7 text-blue-950 sm:grid-cols-2">
        {facts.map((fact) => (
          <li key={fact} className="rounded-2xl bg-white/80 p-4">{fact}</li>
        ))}
      </ul>
    </section>
  );
}

function ShareButtons({ page }) {
  const url = `${seoConfig.baseUrl}${page.path}`;
  const text = encodeURIComponent(page.h1);
  const encodedUrl = encodeURIComponent(url);
  return (
    <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">Share this guide</h2>
      <div className="mt-4 grid w-full max-w-[11rem] grid-cols-3 gap-2 overflow-hidden">
        {[
          { id: "x", label: "Share on X", url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}` },
          { id: "facebook", label: "Share on Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
          { id: "whatsapp", label: "Share on WhatsApp", url: `https://api.whatsapp.com/send?text=${text}%20${encodedUrl}` }
        ].map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
            title={link.label}
            className="focus-ring grid aspect-square w-full max-w-12 min-w-0 place-items-center justify-self-center rounded-full border border-neutral-200 bg-neutral-50 p-0 text-neutral-950 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          >
            <SocialIcon id={link.id} className="h-5 w-5" />
          </a>
        ))}
      </div>
    </div>
  );
}

function SourceList({ sources }) {
  if (!sources?.length) return null;
  return (
    <section id="sources" className="rounded-[1.75rem] border border-neutral-200 bg-neutral-50 p-5 sm:p-6">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950">Sources</h2>
      <ul className="mt-4 grid gap-3 text-sm font-bold leading-6">
        {sources.map((source) => (
          <li key={source.url}>
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="block rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700">
              {source.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FAQ({ items }) {
  if (!items?.length) return null;
  return (
    <section id="faq" className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 sm:p-6">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950">FAQ</h2>
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {items.map((item) => (
          <article key={item.question} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
            <h3 className="text-base font-black text-neutral-950">{item.question}</h3>
            <p className="mt-2 text-sm leading-7 text-neutral-600">{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function RelatedPages({ currentPath }) {
  const related = roboticsSeoClusterPages.filter((page) => page.path !== currentPath).slice(0, 6).map((page) => ({ label: page.h1.replace(/^([^:]+).*/, "$1"), to: page.path }));
  return (
    <section className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 sm:p-6">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950">Related TechniaHQRobot pages</h2>
      <div className="mt-5"><LinkPills items={related} /></div>
    </section>
  );
}

function articleSchema(page, dateModified = "2026-07-07") {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.h1,
    description: page.metaDescription,
    datePublished: "2026-07-07",
    dateModified,
    author: { "@type": "Organization", name: "TechniaHQRobot" },
    publisher: { "@type": "Organization", name: "TechniaHQRobot" },
    mainEntityOfPage: `${seoConfig.baseUrl}${page.path}`,
    image: `${seoConfig.baseUrl}/og-image-tech-ai-2025.png`
  };
}

function faqSchema(page) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (page.faq || []).map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer }
    }))
  };
}

export default function RoboticsSeoClusterPage() {
  const location = useLocation();
  const cleanPath = location.pathname.replace(/\/$/, "") || "/";
  const storedPage = getRoboticsSeoPageByPath(cleanPath);
  if (!storedPage) return <NotFoundArticle />;
  // Older guides store pairs; newer entries use named fields. Both feed the same visible content and schema.
  const page = {
    ...storedPage,
    faq: (storedPage.faq || []).map((item) => Array.isArray(item)
      ? { question: item[0], answer: item[1] }
      : item),
    sources: (storedPage.sources || []).map((item) => Array.isArray(item)
      ? { label: item[0], url: item[1] }
      : item),
  };
  const researchUpdate = getRoboticsResearchEnhancement(cleanPath);
  const dateModified = page.dateModified || (researchUpdate ? "2026-07-10" : "2026-07-07");

  const mainSectionLabels = {
    "/robotics-companies": "Robot Companies",
    "/robotics-data-collection": "Robotics Data Collection"
  };
  const mainSectionLabel = mainSectionLabels[page.path];
  const breadcrumbs = mainSectionLabel
    ? [
        { label: "Home", to: "/" },
        { label: mainSectionLabel, to: page.path }
      ]
    : [
        { label: "Home", to: "/" },
        { label: "Robotics Guides", to: "/robotics" },
        { label: page.h1, to: page.path }
      ];

  return (
    <div className="article-page">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={page.path}
        type="article"
        image={`${seoConfig.baseUrl}/og-image-tech-ai-2025.png`}
        schema={[buildBreadcrumbSchema(breadcrumbs), articleSchema(page, dateModified), ...(page.faq?.length ? [faqSchema(page)] : [])]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader eyebrow="TechniaHQRobot robotics guide" title={page.h1} description={page.metaDescription} />

      <article className="site-shell py-10 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="grid min-w-0 gap-8">
            <div className="soft-card p-5 sm:p-8">
              <div className="flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-neutral-500">
                <span className="rounded-full bg-neutral-950 px-3 py-1 text-white">Last updated {page.dateModifiedLabel || (researchUpdate ? "July 10, 2026" : "July 7, 2026")}</span>
                <span className="rounded-full bg-neutral-100 px-3 py-1">English article</span>
              </div>
              <div className="mt-6 grid gap-4 text-base leading-8 text-neutral-700">
                {page.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </div>

            <ArticleAd placement="top" articleSlug={page.slug} />

            <KeyFacts facts={page.keyFacts} />

            <div className="grid gap-8">
              {page.sections.map((section, index) => (
                <div key={section.heading} className="grid gap-6">
                  <section className="soft-card p-5 sm:p-8">
                    <h2 className="text-2xl font-black tracking-tight text-neutral-950">{section.heading}</h2>
                    <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                      {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                  </section>
                  <InArticleAd index={index + 1} totalSections={page.sections.length} articleSlug={page.slug} />
                </div>
              ))}
            </div>

            {page.tables.map((table) => (
              <section key={table.title} className="grid gap-4">
                <h2 className="text-2xl font-black tracking-tight text-neutral-950">{table.title}</h2>
                <ResponsiveComparisonTable columns={table.columns} rows={table.rows} caption={table.title} ariaLabel={table.title} minWidth="min-w-[760px]" />
              </section>
            ))}

            {!page.hideOutlook && (<section className="soft-card p-5 sm:p-8">
              <h2 className="text-2xl font-black tracking-tight text-neutral-950">What happens next</h2>
              <p className="mt-4 text-base leading-8 text-neutral-700">
                Use the sources below to verify product names, official definitions, event pages and technical claims before quoting this page in a procurement document or public article. If a capability is not publicly confirmed, treat it as not publicly confirmed.
              </p>
            </section>)}

            <ArticleAd placement="bottom" articleSlug={page.slug} />

            <FAQ items={page.faq} />
            <SourceList sources={page.sources} />
            <RelatedPages currentPath={page.path} />
          </div>

          <aside className="grid h-fit gap-5 lg:sticky lg:top-24">
            <ShareButtons page={page} />

          </aside>
        </div>
      </article>
      <CTASection title="Follow TechniaHQRobot for robotics analysis" />
    </div>
  );
}
